import React from 'react';
import { connect } from 'react-redux';
import { LocalizedMessage } from 'intl/LocalizedComponents';
import _map from 'lodash/map';
import _find from 'lodash/find';
import _includes from 'lodash/includes';
import _get from 'lodash/get';
import _reject from 'lodash/reject';
import PropTypes from 'prop-types';
// Core components
import Switch from 'components/core/Switch';
import Typography from 'components/core/Typography';
// Models
import mAlerting from 'models/alerting';
import { serializeAlerting } from 'models/user';
// Custom
import { alertingDataShape, alertingsShape, userAlertingDataShape } from 'constants/PropTypesShapes';
import { sanitizeInt } from 'utils/Tools';
import LoadingDisplayer from 'components/misc/LoadingDisplayer';
import NumberInputField from 'components/inputFields/NumberInputField';

const MEDIA_EMAIL = mAlerting.medias[0];
const MEDIA_SMS = mAlerting.medias[1];

const OFFLINE_TYPE = 'offline';
const POWEROFF_TYPE = 'power_off';

const withDelayAlertings = [
  {
    type: OFFLINE_TYPE,
    minDelay: 0,
    maxDelay: 1440,
  },
  {
    type: POWEROFF_TYPE,
    minDelay: 0,
    maxDelay: 1440,
  },
];

const isAlertingToggled = (userAlertings, alerting, media) => !!_find(userAlertings, { alerting_id: alerting.id, media });

const getAlertingConfigValue = (userAlertings, alerting, media, key, defaultValue) => {
  const userAlerting = _find(userAlertings, { alerting_id: alerting.id, media });
  return _get(userAlerting, ['config', key], defaultValue);
};

const getAlertingDefaultConfig = (alerting) => {
  if (alerting.type === OFFLINE_TYPE) {
    return { delay: 0 };
  }
  if (alerting.type === POWEROFF_TYPE) {
    return { delay: 0 };
  }
  return null;
};

const UserAlertingWithDelayConfig = ({
  userAlertings, alerting, media, minDelay, maxDelay, onChange,
}) => (
  <div className="mx-3" style={{ maxWidth: '75px' }}>
    <NumberInputField
      margin="none"
      min={minDelay}
      max={maxDelay}
      value={getAlertingConfigValue(userAlertings, alerting, media, 'delay', 0) / 60}
      disabled={!isAlertingToggled(userAlertings, alerting, media)}
      adornments={{
        end: (
          <Typography type="adornment">
            <LocalizedMessage id="info.abbrMinutes" />
          </Typography>
        ),
      }}
      sanitizer={sanitizeInt}
      onChange={(value) => onChange({ delay: value * 60 })}
    />
  </div>
);

UserAlertingWithDelayConfig.propTypes = {
  userAlertings: PropTypes.arrayOf(userAlertingDataShape).isRequired,
  alerting: alertingDataShape.isRequired,
  media: PropTypes.oneOf([MEDIA_EMAIL, MEDIA_SMS]).isRequired,
  minDelay: PropTypes.number.isRequired,
  maxDelay: PropTypes.number.isRequired,
  onChange: PropTypes.func.isRequired,
};

const UserAlertings = ({ alertings, userAlertings, onChange }) => {
  const handleToggleAlerting = (toggled, alertingId, media, config) => {
    const newAlertings = toggled
      ? [...userAlertings, serializeAlerting({ alertingId, media, config })]
      : _reject(userAlertings, { alerting_id: alertingId, media });

    onChange(newAlertings);
  };

  const handleAlertingConfigChange = (alertingId, media, config) => {
    const newAlertings = _map(userAlertings, (userAlerting) => (
      (userAlerting.alerting_id === alertingId && userAlerting.media === media) ? {
        ...userAlerting,
        config,
      } : userAlerting
    ));

    onChange(newAlertings);
  };

  const { loading, elements } = alertings;

  return (
    <LoadingDisplayer loading={loading}>
      <div className="pt-3">
        <div className="row">
          <div className="col-12 col-md-6">
            <strong><LocalizedMessage id="info.meAlerting" /></strong>
          </div>
          <div className="col-3 hidden-sm-down">
            <em><LocalizedMessage id="info.byEmail" /></em>
          </div>
          <div className="col-3 hidden-sm-down">
            <em><LocalizedMessage id="info.bySms" /></em>
          </div>
        </div>
        <div className="row pt-3">
          <div className="col-12 p-0">
            <div className="box-divider" />
          </div>
        </div>
        {_map(elements, (alerting) => {
          const withDelayAlerting = _find(withDelayAlertings, ['type', alerting.type]);
          return (
            <div key={alerting.id}>
              <div className="row pt-2 align-items-center">
                <div className="col-12 col-md-6">
                  <LocalizedMessage id={`alerting.type.${alerting.type}`} />
                  <div className="row pt-3 hidden-md-up">
                    {_includes(alerting.medias, MEDIA_EMAIL) && (
                      <div className="col-12 d-flex align-items-center justify-content-between">
                        <div>
                          <em><LocalizedMessage id="info.byEmail" /></em>
                        </div>
                        <div className="d-flex align-items-center">
                          {withDelayAlerting && (
                            <UserAlertingWithDelayConfig
                              userAlertings={userAlertings}
                              alerting={alerting}
                              media={MEDIA_EMAIL}
                              minDelay={withDelayAlerting.minDelay}
                              maxDelay={withDelayAlerting.maxDelay}
                              onChange={(config) => handleAlertingConfigChange(alerting.id, MEDIA_EMAIL, config)}
                            />
                          )}
                          <Switch
                            checked={isAlertingToggled(userAlertings, alerting, MEDIA_EMAIL)}
                            edge="end"
                            onChange={(checked) => handleToggleAlerting(checked, alerting.id, MEDIA_EMAIL, getAlertingDefaultConfig(alerting))}
                          />
                        </div>
                      </div>
                    )}
                    {_includes(alerting.medias, MEDIA_SMS) && (
                      <div className="col-12 pt-2 d-flex align-items-center justify-content-between">
                        <div>
                          <em><LocalizedMessage id="info.bySms" /></em>
                        </div>
                        <div className="d-flex align-items-center">
                          {withDelayAlerting && (
                            <UserAlertingWithDelayConfig
                              userAlertings={userAlertings}
                              alerting={alerting}
                              media={MEDIA_SMS}
                              minDelay={withDelayAlerting.minDelay}
                              maxDelay={withDelayAlerting.maxDelay}
                              onChange={(config) => handleAlertingConfigChange(alerting.id, MEDIA_SMS, config)}
                            />
                          )}
                          <Switch
                            checked={isAlertingToggled(userAlertings, alerting, MEDIA_SMS)}
                            edge="end"
                            onChange={(checked) => handleToggleAlerting(checked, alerting.id, MEDIA_SMS, getAlertingDefaultConfig(alerting))}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                <div className="col-3 hidden-sm-down">
                  {_includes(alerting.medias, MEDIA_EMAIL) && (
                    <div className="d-flex align-items-center">
                      <Switch
                        checked={isAlertingToggled(userAlertings, alerting, MEDIA_EMAIL)}
                        edge="end"
                        onChange={(checked) => handleToggleAlerting(checked, alerting.id, MEDIA_EMAIL, getAlertingDefaultConfig(alerting))}
                      />
                      {withDelayAlerting && (
                        <UserAlertingWithDelayConfig
                          userAlertings={userAlertings}
                          alerting={alerting}
                          media={MEDIA_EMAIL}
                          minDelay={withDelayAlerting.minDelay}
                          maxDelay={withDelayAlerting.maxDelay}
                          onChange={(config) => handleAlertingConfigChange(alerting.id, MEDIA_EMAIL, config)}
                        />
                      )}
                    </div>
                  )}
                </div>
                <div className="col-3 hidden-sm-down">
                  {_includes(alerting.medias, MEDIA_SMS) && (
                    <div className="d-flex align-items-center">
                      <Switch
                        checked={isAlertingToggled(userAlertings, alerting, MEDIA_SMS)}
                        edge="end"
                        onChange={(checked) => handleToggleAlerting(checked, alerting.id, MEDIA_SMS, getAlertingDefaultConfig(alerting))}
                      />
                      {withDelayAlerting && (
                        <UserAlertingWithDelayConfig
                          userAlertings={userAlertings}
                          alerting={alerting}
                          media={MEDIA_SMS}
                          minDelay={withDelayAlerting.minDelay}
                          maxDelay={withDelayAlerting.maxDelay}
                          onChange={(config) => handleAlertingConfigChange(alerting.id, MEDIA_SMS, config)}
                        />
                      )}
                    </div>
                  )}
                </div>
              </div>
              <div className="row pt-3 pt-md-2">
                <div className="col-12 p-0">
                  <div className="box-divider" />
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </LoadingDisplayer>
  );
};

UserAlertings.propTypes = {
  alertings: alertingsShape.isRequired,
  userAlertings: PropTypes.arrayOf(userAlertingDataShape).isRequired,
  onChange: PropTypes.func.isRequired,
};

const mapStateToProps = (state) => ({
  alertings: state.alerting.list,
});

module.exports = connect(
  mapStateToProps,
  null,
)(UserAlertings);



// WEBPACK FOOTER //
// ./routes/app/routes/users/assets/main/UserAlertings.js